import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { AddressFields } from '@/components/GooglePlacesAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { STEAMSHIP_LINES } from '@/constants/steamshipLines';
import { RAIL_RAMPS } from '@/constants/columbusTerminals';
import { CONTAINER_SIZES } from '@/constants/containerTypes';
import { OVERWEIGHT_THRESHOLD, APPOINTMENT_TYPES } from '@/constants/commercialLogic';
import { AlertTriangle, Calculator, ArrowRight, ArrowLeft, Package, MapPin, FileText, Ship, Truck, DollarSign, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RateRequestForm({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = type selection, 2 = details, 3 = result
  
  // Request type selection
  const [requestType, setRequestType] = useState(null); // 'Import' or 'Export'
  
  // Container details
  const [containerSize, setContainerSize] = useState("40' Dry");
  const [numberOfContainers, setNumberOfContainers] = useState(1);
  const [estimatedWeight, setEstimatedWeight] = useState('');
  const [hazmat, setHazmat] = useState(false);
  
  // Common fields
  const [commodity, setCommodity] = useState('');
  const [steamshipLine, setSteamshipLine] = useState('');
  const [railRamp, setRailRamp] = useState('');
  
  // Address for calculation
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    latitude: null,
    longitude: null
  });
  
  // Import-specific fields
  const [consignee, setConsignee] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [deliveryType, setDeliveryType] = useState('Live Unload');
  const [lastFreeDay, setLastFreeDay] = useState('');
  
  // Export-specific fields
  const [shipperName, setShipperName] = useState('');
  const [portOfLoading, setPortOfLoading] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [cutoffDate, setCutoffDate] = useState('');
  const [loadingType, setLoadingType] = useState('Live Load');
  
  // Notes
  const [specialInstructions, setSpecialInstructions] = useState('');
  
  // Rate result
  const [rateResult, setRateResult] = useState(null);
  
  // Check if overweight
  const isOverweight = parseInt(estimatedWeight) >= OVERWEIGHT_THRESHOLD;
  
  // Handle address change
  const handleAddressChange = (addressData) => {
    setAddress({
      street: addressData.street || address.street,
      city: addressData.city || address.city,
      state: addressData.state || address.state,
      zip: addressData.zip || address.zip,
      latitude: addressData.latitude || address.latitude,
      longitude: addressData.longitude || address.longitude
    });
  };
  
  // Submit rate request
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!address.city || !address.zip) {
      toast.error('Please enter a delivery/pickup address');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        request_type: requestType,
        container_size: containerSize,
        estimated_weight_lbs: parseInt(estimatedWeight) || null,
        number_of_containers: numberOfContainers,
        hazmat,
        commodity,
        steamship_line: steamshipLine,
        rail_ramp: railRamp,
        city: address.city,
        state: address.state,
        zip_code: address.zip,
        special_instructions: specialInstructions
      };
      
      if (requestType === 'Import') {
        payload.consignee_name = consignee.name;
        payload.consignee_phone = consignee.phone;
        payload.consignee_email = consignee.email;
        payload.delivery_type = deliveryType;
        payload.last_free_day = lastFreeDay || null;
      } else {
        payload.shipper_name = shipperName;
        payload.port_of_loading = portOfLoading;
        payload.destination_country = destinationCountry;
        payload.cutoff_date = cutoffDate || null;
        payload.loading_type = loadingType;
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/rate-request`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRateResult(response.data);
      setStep(3);
      toast.success('Rate request submitted successfully!');
    } catch (error) {
      console.error('Rate request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rate request');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset form
  const handleNewRequest = () => {
    setStep(1);
    setRequestType(null);
    setRateResult(null);
    setContainerSize("40' Dry");
    setNumberOfContainers(1);
    setEstimatedWeight('');
    setHazmat(false);
    setCommodity('');
    setSteamshipLine('');
    setRailRamp('');
    setAddress({ street: '', city: '', state: '', zip: '', latitude: null, longitude: null });
    setConsignee({ name: '', phone: '', email: '' });
    setDeliveryType('Live Unload');
    setLastFreeDay('');
    setShipperName('');
    setPortOfLoading('');
    setDestinationCountry('');
    setCutoffDate('');
    setLoadingType('Live Load');
    setSpecialInstructions('');
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar user={user} />
      
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="rate-request-page">
        <h1 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase mb-2">
          <span className="text-[#00A6E8]">RATE</span>
          <span className="text-white"> REQUEST</span>
        </h1>
        <p className="text-slate-400 mb-8">Get an instant quote or request a custom rate</p>
        
        {/* Step 1: Type Selection */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-1-type-selection">
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8">
              <h2 className="text-white font-bold text-xl mb-6 text-center">
                What type of shipment?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => { setRequestType('Import'); setStep(2); }}
                  className={`p-6 rounded-lg border-2 transition-all hover:scale-[1.02] ${
                    requestType === 'Import'
                      ? 'border-[#00A6E8] bg-[#00A6E8]/10'
                      : 'border-white/10 hover:border-[#00A6E8]/50'
                  }`}
                  data-testid="select-import-btn"
                >
                  <Truck className="mx-auto text-[#00A6E8] mb-4" size={48} />
                  <h3 className="text-white font-bold text-lg mb-2">IMPORT</h3>
                  <p className="text-slate-400 text-sm">
                    Delivery from rail ramp to consignee location
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => { setRequestType('Export'); setStep(2); }}
                  className={`p-6 rounded-lg border-2 transition-all hover:scale-[1.02] ${
                    requestType === 'Export'
                      ? 'border-[#FFA726] bg-[#FFA726]/10'
                      : 'border-white/10 hover:border-[#FFA726]/50'
                  }`}
                  data-testid="select-export-btn"
                >
                  <Ship className="mx-auto text-[#FFA726] mb-4" size={48} />
                  <h3 className="text-white font-bold text-lg mb-2">EXPORT</h3>
                  <p className="text-slate-400 text-sm">
                    Pickup from shipper and delivery to rail ramp
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Step 2: Details Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="step-2-details-form">
            {/* Back button */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft size={16} className="mr-2" /> Change Type
            </Button>
            
            {/* Type indicator */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
              requestType === 'Import'
                ? 'bg-[#00A6E8]/10 text-[#00A6E8]'
                : 'bg-[#FFA726]/10 text-[#FFA726]'
            }`}>
              {requestType === 'Import' ? <Truck size={16} /> : <Ship size={16} />}
              {requestType} Rate Request
            </div>
            
            {/* Container Details */}
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
              <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                <Package className={requestType === 'Import' ? 'text-[#00A6E8]' : 'text-[#FFA726]'} size={20} />
                Container Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Container Size *</Label>
                  <Select value={containerSize} onValueChange={setContainerSize}>
                    <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="container-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[200px]">
                      {CONTAINER_SIZES.map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Number of Containers</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={numberOfContainers}
                    onChange={(e) => setNumberOfContainers(parseInt(e.target.value) || 1)}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    data-testid="num-containers-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Estimated Weight (lbs)</Label>
                  <Input
                    type="number"
                    value={estimatedWeight}
                    onChange={(e) => setEstimatedWeight(e.target.value)}
                    placeholder="40000"
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    data-testid="weight-input"
                  />
                  {isOverweight && (
                    <p className="text-yellow-400 text-xs mt-1 flex items-center gap-1">
                      <AlertTriangle size={12} /> Overweight charge (+$175/container)
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={hazmat}
                    onCheckedChange={setHazmat}
                    className="border-white/30"
                  />
                  <span className="text-slate-300">Hazardous Materials</span>
                  {hazmat && <span className="text-yellow-400 text-xs">(+$175/container)</span>}
                </label>
              </div>
            </div>
            
            {/* Shipment Details */}
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
              <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                <FileText className={requestType === 'Import' ? 'text-[#00A6E8]' : 'text-[#FFA726]'} size={20} />
                Shipment Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Commodity</Label>
                  <Input
                    value={commodity}
                    onChange={(e) => setCommodity(e.target.value)}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    data-testid="commodity-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Steamship Line (SSL)</Label>
                  <Select value={steamshipLine} onValueChange={setSteamshipLine}>
                    <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="ssl-select">
                      <SelectValue placeholder="Select SSL" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[300px]">
                      {STEAMSHIP_LINES.map((ssl) => (
                        <SelectItem key={ssl} value={ssl}>{ssl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Rail Ramp</Label>
                <Select value={railRamp} onValueChange={setRailRamp}>
                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="rail-ramp-select">
                    <SelectValue placeholder="Select Rail Ramp" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                    {RAIL_RAMPS.map((ramp) => (
                      <SelectItem key={ramp} value={ramp}>{ramp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dynamic Fields based on Import/Export */}
            {requestType === 'Import' ? (
              <>
                {/* Import: Consignee & Delivery */}
                <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
                  <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                    <MapPin className="text-[#00A6E8]" size={20} />
                    Consignee & Delivery
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300">Consignee Name</Label>
                      <Input
                        value={consignee.name}
                        onChange={(e) => setConsignee({...consignee, name: e.target.value})}
                        className="mt-2 bg-[#1E293B] border-white/10 text-white"
                        data-testid="consignee-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={consignee.phone}
                        onChange={(e) => setConsignee({...consignee, phone: e.target.value})}
                        className="mt-2 bg-[#1E293B] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input
                        type="email"
                        value={consignee.email}
                        onChange={(e) => setConsignee({...consignee, email: e.target.value})}
                        className="mt-2 bg-[#1E293B] border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-white/10">
                    <Label className="text-slate-300 mb-3 block">Delivery Address *</Label>
                    <AddressFields
                      formData={address}
                      onChange={handleAddressChange}
                      required={true}
                      testIdPrefix="delivery"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Delivery Type</Label>
                      <Select value={deliveryType} onValueChange={setDeliveryType}>
                        <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                          {APPOINTMENT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Last Free Day</Label>
                      <Input
                        type="date"
                        value={lastFreeDay}
                        onChange={(e) => setLastFreeDay(e.target.value)}
                        className="mt-2 bg-[#1E293B] border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Export: Consignee & Loading */}
                <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
                  <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                    <Truck className="text-[#FFA726]" size={20} />
                    Shipper & Loading
                  </h3>
                  
                  <div>
                    <Label className="text-slate-300">Shipper Name</Label>
                    <Input
                      value={shipperName}
                      onChange={(e) => setShipperName(e.target.value)}
                      className="mt-2 bg-[#1E293B] border-white/10 text-white"
                      data-testid="shipper-name-input"
                    />
                  </div>
                  
                  <div className="pt-4 border-t border-white/10">
                    <Label className="text-slate-300 mb-3 block">Pickup/Loading Address *</Label>
                    <AddressFields
                      formData={address}
                      onChange={handleAddressChange}
                      required={true}
                      testIdPrefix="pickup"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Loading Type</Label>
                    <Select value={loadingType} onValueChange={setLoadingType}>
                      <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                        {APPOINTMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Export: Destination */}
                <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
                  <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                    <Ship className="text-[#FFA726]" size={20} />
                    Destination Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Port of Loading</Label>
                      <Select value={portOfLoading} onValueChange={setPortOfLoading}>
                        <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white">
                          <SelectValue placeholder="Select Port" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                          {RAIL_RAMPS.map((ramp) => (
                            <SelectItem key={ramp} value={ramp}>{ramp}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Destination Country</Label>
                      <Input
                        value={destinationCountry}
                        onChange={(e) => setDestinationCountry(e.target.value)}
                        className="mt-2 bg-[#1E293B] border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Cutoff Date</Label>
                    <Input
                      type="date"
                      value={cutoffDate}
                      onChange={(e) => setCutoffDate(e.target.value)}
                      className="mt-2 bg-[#1E293B] border-white/10 text-white w-48"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Special Instructions */}
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6">
              <Label className="text-slate-300">Special Instructions / Notes</Label>
              <Textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                className="mt-2 bg-[#1E293B] border-white/10 text-white"
                rows={3}
                data-testid="instructions-input"
              />
            </div>
            
            {/* Submit */}
            <Button 
              type="submit" 
              disabled={loading}
              className={`w-full font-bold uppercase tracking-wider hover:scale-[1.02] transition-all py-6 ${
                requestType === 'Import'
                  ? 'bg-[#00A6E8] text-[#0A0E1A] hover:bg-[#00A6E8]/90'
                  : 'bg-[#FFA726] text-[#0A0E1A] hover:bg-[#FFB74D]'
              }`}
              data-testid="submit-rate-request-btn"
            >
              {loading ? 'Calculating...' : (
                <>
                  <Calculator size={20} className="mr-2" />
                  Get Rate Quote
                </>
              )}
            </Button>
          </form>
        )}
        
        {/* Step 3: Result */}
        {step === 3 && rateResult && (
          <div className="space-y-6" data-testid="step-3-result">
            {/* Success message */}
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-green-400 font-semibold">Rate Request Submitted</p>
                <p className="text-slate-400 text-sm">
                  Your request has been logged as <span className="text-white font-mono">{rateResult.load_number}</span>
                </p>
              </div>
            </div>
            
            {/* Rate breakdown */}
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
                  <DollarSign className="text-green-400" size={20} />
                  Rate Quote
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  rateResult.zone === 'C'
                    ? 'bg-yellow-400/10 text-yellow-400'
                    : 'bg-green-400/10 text-green-400'
                }`}>
                  Zone {rateResult.zone}
                </span>
              </div>
              
              {/* Distance */}
              {rateResult.distance_miles && (
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin size={16} />
                  <span>Distance: {rateResult.distance_miles} miles from Columbus</span>
                </div>
              )}
              
              {/* Zone C Warning */}
              {rateResult.zone === 'C' && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-0.5" size={16} />
                    <div className="text-sm">
                      <p className="text-yellow-400 font-semibold">Custom Quote Required</p>
                      <p className="text-slate-400 mt-1">
                        Your destination is beyond Zone B (40 miles). A DLT representative will contact you with a custom Line Haul rate.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Charges breakdown */}
              {rateResult.estimated_charges && (
                <div className="space-y-3 border-t border-white/10 pt-4">
                  {rateResult.estimated_charges.line_haul_per_container && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">
                        Line Haul (${rateResult.estimated_charges.line_haul_per_container} × {numberOfContainers})
                      </span>
                      <span className="text-white font-semibold">
                        ${rateResult.estimated_charges.line_haul_per_container * numberOfContainers}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Chassis Minimum ($90 × {numberOfContainers})</span>
                    <span className="text-white">${rateResult.estimated_charges.chassis_minimum}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Chassis Split ($85 × {numberOfContainers})</span>
                    <span className="text-white">${rateResult.estimated_charges.chassis_split}</span>
                  </div>
                  
                  {rateResult.estimated_charges.hazmat > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-yellow-400">Hazmat ($175 × {numberOfContainers})</span>
                      <span className="text-yellow-400">${rateResult.estimated_charges.hazmat}</span>
                    </div>
                  )}
                  
                  {rateResult.estimated_charges.overweight > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-yellow-400">Overweight ($175 × {numberOfContainers})</span>
                      <span className="text-yellow-400">${rateResult.estimated_charges.overweight}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="text-white font-semibold text-lg">
                      {rateResult.zone === 'C' ? 'Accessorials Total:' : 'Estimated Total:'}
                    </span>
                    <span className={`font-bold text-2xl ${
                      rateResult.zone === 'C' ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {rateResult.estimated_charges.total 
                        ? `$${rateResult.estimated_charges.total}`
                        : 'Pending'
                      }
                    </span>
                  </div>
                  
                  {rateResult.zone === 'C' && (
                    <p className="text-slate-500 text-xs">
                      * Line Haul will be added once custom quote is provided
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleNewRequest}
                variant="outline"
                className="flex-1 border-white/20 text-slate-300"
              >
                <ArrowLeft size={16} className="mr-2" />
                New Rate Request
              </Button>
              
              <Button
                onClick={() => navigate('/my-shipments')}
                className="flex-1 bg-[#00A6E8] text-[#0A0E1A]"
              >
                View My Shipments
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
